import React, { ReactElement, useContext } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import './home.scss';
import { AlbumType } from 'types';
import { ArtistContext } from 'context/ArtistContext';
import { Slide } from 'react-awesome-reveal';
import useViewport from 'hooks/useViewport';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-cards';
import { EffectCards } from 'swiper';

const GET_ALBUMS = gql`
  query GET_ALBUMS {
    albums {
      id
      albumUrl
      title
      releaseDate
      smartBuyLink
      featureHomePage
      albumCover {
        id
        url
      }
      trackListing {
        raw
        html
        markdown
        text
      }
    }
  }
`;

interface FeatureAlbumProps {
  albumMap?: boolean;
}

function FeatureAlbum(props: FeatureAlbumProps): ReactElement {
  const { loading, data } = useQuery(GET_ALBUMS);
  const artist = useContext(ArtistContext);
  const { isMobile } = useViewport();
  if (!artist) return <div />;
  if (loading) return <div />;
  const { albums } = data;
  const { isSpanish } = artist;
  const { albumMap } = props;

  return (
    <>
      {albums
        .filter(
          (album: { featureHomePage: boolean }) =>
            album.featureHomePage === true,
        )
        .map((albumItem: AlbumType) => (
          <section
            className="gradient-bkg px-2 mx-0 -mt-2"
            key={albumItem.albumUrl}>
            <section className="container py-5">
              <div className="grid">
                <div className="col-12">
                  <Slide className="col-12" direction="up" triggerOnce>
                    <h3 className="text-3xl md:text-6xl mt-0 mb-4 text-center uppercase">
                      {albumItem.title}
                    </h3>
                  </Slide>
                </div>
                <div className="col-12 md:col-8  mx-auto">
                  {!!albumItem.albumUrl && (
                    <Link
                      to={`/music/${albumItem.albumUrl}`}
                      className="mx-auto max-w-max block no-underline">
                      {!!albumItem.albumCover?.url && (
                        <Slide className="w-full" direction="up" triggerOnce>
                          <img
                            src={albumItem.albumCover.url}
                            alt={(albumItem.title && albumItem.title) || ''}
                            className="mx-auto mb-0 w-12 block theme-box-shadow border-round"
                            style={{
                              maxHeight: '400px',
                              maxWidth: '400px',
                              objectFit: 'cover',
                              aspectRatio: 1,
                            }}
                          />
                        </Slide>
                      )}
                    </Link>
                  )}
                </div>
                <div className="col-12 md:col-8 mx-auto my-auto">
                  {!!albumItem.trackListing && (
                    <div className="parse-block track-list text-center">
                      {parse(albumItem.trackListing.html)}
                    </div>
                  )}
                  {!!albumItem.smartBuyLink && (
                    <a
                      href={albumItem.smartBuyLink}
                      target="_blank"
                      rel="noreferrer"
                      className="theme-bg-white theme-border-blk border-1 px-8 px-4 theme-button mx-auto max-w-max block no-underline">
                      {isSpanish ? 'Escucha' : 'Listen'}
                    </a>
                  )}
                </div>
              </div>
            </section>
          </section>
        ))}
      {albumMap && (
        <section className="gradient-bkg px-2 mx-0 -mt-2">
          <section className="container py-5">
            <h2 className="text-6xl text-center">
              {isSpanish ? 'Música' : 'Music'}
            </h2>
            {isMobile ? (
              <Swiper
                effect="cards"
                grabCursor
                modules={[EffectCards]}
                className="album-swiper">
                {albums.map((albumItem: AlbumType) => (
                  <SwiperSlide key={albumItem.albumUrl}>
                    <a
                      href={albumItem.smartBuyLink}
                      target="_blank"
                      rel="noreferrer"
                      className="max-w-max no-underline">
                      {!!albumItem.albumCover?.url && (
                        <img
                          src={albumItem.albumCover.url}
                          alt={(albumItem.title && albumItem.title) || ''}
                          className="mx-auto mb-0  block border-round"
                          style={{
                            maxHeight: '400px',
                            maxWidth: '400px',
                            objectFit: 'cover',
                            aspectRatio: 1,
                          }}
                        />
                      )}
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="grid">
                {albums.map((albumItem: AlbumType) => (
                  <div
                    className="col-8 col:4 lg:col-4 xl:col-2 mx-auto"
                    key={albumItem.albumUrl}>
                    <Slide className="col-12" direction="up" triggerOnce>
                      {!!albumItem.albumUrl && (
                        <a
                          href={albumItem.smartBuyLink}
                          target="_blank"
                          rel="noreferrer"
                          className="mx-auto max-w-max block no-underline">
                          {!!albumItem.albumCover?.url && (
                            <img
                              src={albumItem.albumCover.url}
                              alt={(albumItem.title && albumItem.title) || ''}
                              className="mx-auto mb-0 w-12 block theme-box-shadow border-round"
                              style={{
                                maxHeight: '400px',
                                maxWidth: '400px',
                                objectFit: 'cover',
                                aspectRatio: 1,
                              }}
                            />
                          )}
                        </a>
                      )}
                      <h3 className="text-xs mt-0 mb-4 text-center uppercase">
                        {albumItem.title}
                      </h3>
                    </Slide>
                  </div>
                ))}
              </div>
            )}
          </section>
        </section>
      )}
    </>
  );
}

export default FeatureAlbum;
